import { useEffect, useRef, useState } from 'react';

import Stack from '@liquidweb-dev/undercurrent/Stack';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import { useLeftHandLayout } from 'components/LeftHandLayout';
import CollapsedHeader from './PageLayoutCollapsedHeader';
import PageLayoutContent from './PageLayoutContent';
import PageLayoutHeader from './PageLayoutHeader';
import PageLayoutNote from './PageLayoutNote';
import { type PageLayoutSimpleProps } from './PageLayoutSimple';

export type PageLayoutDynamicProps = PageLayoutSimpleProps;

const PageLayout = ({
	actionsSlot,
	alert,
	attributesSlot,
	breadcrumbsSlot,
	children,
	description,
	Icon,
	iconBoxProps,
	isLoading,
	isTitleSaveBusy,
	note,
	noteProps,
	onTitleSave,
	title,
	sx,
	...rest
}: PageLayoutDynamicProps) => {
	const { screenSize } = useLeftHandLayout();
	const [isAtTop, setIsAtTop] = useState(true);
	const standardHeaderRef = useRef<HTMLDivElement | null>(null);
	const standardHeaderHeight = standardHeaderRef.current?.clientHeight;

	const isSmallScreen = screenSize === 'small';
	useEffect(() => {
		function handleScrollChange() {
			setIsAtTop(window.scrollY < (standardHeaderHeight || 150));
		}

		window.addEventListener('scroll', handleScrollChange);
		return () => {
			window.removeEventListener('scroll', handleScrollChange);
		};
	}, [standardHeaderHeight]);

	return (
		<Stack
			sx={combineSx(
				{
					position: 'relative',
					flexGrow: 1,
				},
				sx,
			)}
			{...rest}
		>
			{note && <PageLayoutNote noteProps={noteProps}>{note}</PageLayoutNote>}

			{!isSmallScreen && (
				<CollapsedHeader
					actionsSlot={actionsSlot}
					attributesSlot={attributesSlot}
					isLoading={isLoading}
					show={!isAtTop}
					title={title}
				/>
			)}

			<PageLayoutHeader
				actionsSlot={actionsSlot}
				alert={alert}
				attributesSlot={attributesSlot}
				breadcrumbsSlot={breadcrumbsSlot}
				description={description}
				Icon={Icon}
				iconBoxProps={iconBoxProps}
				isLoading={isLoading}
				isTitleSaveBusy={isTitleSaveBusy}
				onTitleSave={onTitleSave}
				title={title}
				sx={{
					opacity: isAtTop || isSmallScreen ? 1 : 0,
					visibility: isAtTop || isSmallScreen ? 'visible' : 'hidden',
				}}
				ref={standardHeaderRef}
			/>

			<PageLayoutContent sx={{ flexGrow: 1 }}>{children}</PageLayoutContent>
		</Stack>
	);
};

export default PageLayout;
